import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  viewTransition,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('close', this.#unlockPageScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('close', this.#unlockPageScroll);
    this.#unlockPageScroll();
  }

  /**
   * Lock and unlock the page behind the dialog. The dialog carries a `scroll-lock` attribute, but
   * the theme only honours it on `<details>` elements, so scrolling the zoomed gallery chained
   * through to the product page and left it scrolled somewhere else once the dialog closed.
   *
   * Hiding the page's overflow also takes its scrollbar away, which widens the viewport and
   * reflows the page sideways. A matching gutter holds the layout still, so neither the opening
   * nor the closing animation carries a sideways jump with it.
   */
  #lockPageScroll = () => {
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    document.documentElement.style.setProperty('--scroll-lock-gutter', `${scrollbarWidth}px`);
    document.documentElement.setAttribute('scroll-lock', '');
  };

  #unlockPageScroll = () => {
    const unlock = () => {
      document.documentElement.removeAttribute('scroll-lock');
      document.documentElement.style.removeProperty('--scroll-lock-gutter');
    };

    // Giving the scrollbar back mid-animation would reflow the page underneath it, so wait for
    // any transition that is still running.
    if (viewTransition.current) viewTransition.current.then(unlock, unlock);
    else unlock();
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    // Locked before the transition captures the old state, so both snapshots share one layout and
    // the page cannot reflow part-way through the animation.
    this.#lockPageScroll();

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    // Animate the photo itself, not its container: the zoomed container fills the viewport, so
    // morphing it would grow an empty full-screen box instead of zooming the product into place.
    // Resolved inside the transition below, once the image has been magnified.
    /** @type {HTMLElement} */
    let targetTransitionElement = targetImage;

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    // Marks the opening transition so the dialog's backdrop is opaque and painted at once, rather
    // than fading in behind the image. Set on the root because it also has to select the
    // ::view-transition pseudo-elements, which are children of the root.
    document.documentElement.classList.add('zoom-dialog-opening');

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetTransitionElement = getTransitionElement(targetImage);
      targetTransitionElement.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.classList.remove('zoom-dialog-opening');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetTransitionElement.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    // Decode the high-resolution file first, then point the existing image at it. Swapping the
    // element instead made the image visibly pop right after the open animation, and dropped the
    // width/height attributes and the `ref` that drag-zoom-wrapper relies on.
    const loader = new Image();

    loader.onload = () => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = highResolutionUrl;
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    loader.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    // Follow the gallery with the rail. Only a few thumbnails fit beside the image, so without
    // this the active one moves off-screen as soon as you scroll past the first few and the
    // customer loses track of where they are.
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
      inline: 'center',
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   *
   * @param {Event} [event] - The event that asked for the close, when there was one.
   */
  async close(event) {
    // A click on a zoomable image zooms it instead of closing. The theme's declarative `on:click`
    // handlers are registered on `document` in the capture phase, so the drag-zoom wrapper cannot
    // stop the event from reaching us — the decision has to be made here.
    if (isHandledByZoom(event)) return;

    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    // The thumbnails slide out alongside the image shrinking back, rather than before it. Waiting
    // for that animation first left the dialog sitting still for a beat after the close was
    // clicked, which read as the close being unresponsive.
    dialog.classList.add('dialog--closed');

    // Animate the photo itself, for the same reason as in `open()`.
    const sourceTransitionElement = getTransitionElement(mostVisibleElement);

    sourceTransitionElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      sourceTransitionElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    this.#unlockPageScroll();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Whether a click belongs to the drag-zoom wrapper — a zoom toggle, or the click that ends a pan —
 * rather than to the dialog's close behaviour.
 *
 * @param {Event} [event] - The event that asked for the close.
 * @returns {boolean}
 */
function isHandledByZoom(event) {
  if (!(event instanceof MouseEvent)) return false;

  const target = event.composedPath?.()[0] ?? document.elementFromPoint(event.clientX, event.clientY);
  const wrapper = target instanceof Element ? target.closest('drag-zoom-wrapper') : null;

  /** @type {{ handlesClick?: (event: MouseEvent) => boolean } | null} */
  const dragZoomWrapper = /** @type {any} */ (wrapper);

  return typeof dragZoomWrapper?.handlesClick === 'function' && dragZoomWrapper.handlesClick(event);
}

/**
 * Get the element that should carry the view transition for a media container: the image itself
 * when there is one, so the transition morphs the visible photo rather than its container.
 * @param {HTMLElement} mediaContainer - The media container element.
 * @returns {HTMLElement} The element to animate.
 */
function getTransitionElement(mediaContainer) {
  // A magnified image overflows its wrapper, and a view transition snapshot is not clipped by
  // ancestors — so capturing the image would animate a box wider than the stage, and the parts
  // outside it would vanish when the transition ended. The wrapper clips, so its snapshot is
  // exactly what is on screen.
  const zoomedWrapper = mediaContainer.querySelector('drag-zoom-wrapper[zoomed]');

  if (zoomedWrapper instanceof HTMLElement) return zoomedWrapper;

  const image = mediaContainer.querySelector('img.product-media__image');

  return image instanceof HTMLElement ? image : mediaContainer;
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
